import { createElement, type CSSProperties } from 'react';
import { PlanAddonTypes } from '../../../../../modules/renew-plan/v1/typings';
import { PurchaseBlockerModalType } from '../typings';

// Zoho CRM
import ZohoPaywallImage from '../../../../assets/img/blocker-modals/zoho-crm-paywall.webp';
import { ReactComponent as ZohoCrmIcon } from '../../../../assets/svg/blocker-modals/zoho-crm-icon.svg';
import { ReactComponent as CloudCheckIcon } from '../../../../icons/cloud-check.svg';
import { ReactComponent as TopSideIcon } from '../../../../icons/top-side.svg';
import { ReactComponent as FlowChartIcon } from '../../../../icons/flow-chart.svg';

// LeadSquared
import LeadSquaredPaywallImage from '../../../../assets/img/blocker-modals/leadsquared-paywall.webp';
import { ReactComponent as LeadSquaredIcon } from '../../../../assets/svg/blocker-modals/leadsquared-icon.svg';
import SingleTickIcon from '../../../../icons/SingleTick';

// Salesforce
import SalesforcePaywallImage from '../../../../assets/img/blocker-modals/salesforce-paywall.webp';
import { ReactComponent as SalesforceIcon } from '../../../../assets/svg/blocker-modals/salesforce-con.svg';

// WooCommerce
import WoocommerceDoubletickImage from '../../../../assets/img/blocker-modals/doubletick-woocommerce.png';
import WoocommerceImage from '../../../../assets/img/blocker-modals/woocommerce.png';
import { ReactComponent as WooCommerceIcon } from '../../../../assets/svg/blocker-modals/woocommerce.svg';
import { ReactComponent as WooIcon1 } from '../../../../assets/svg/blocker-modals/icon_1.svg';
import { ReactComponent as WooIcon2 } from '../../../../assets/svg/blocker-modals/icon_2.svg';
import { ReactComponent as WooIcon3 } from '../../../../assets/svg/blocker-modals/icon_3.svg';
import { ReactComponent as WooIcon4 } from '../../../../assets/svg/blocker-modals/icon_4.svg';

// Activity Logs
import ActivityLogsPNG from '../../../../assets/img/blocker-modals/activity-logs-modal.png';
import { ReactComponent as ActivityLogsIcon } from '../../../../assets/svg/blocker-modals/ActivityLogsIcon.svg';
import { ReactComponent as ENTFilter } from '../../../../assets/svg/blocker-modals/ENTFilter.svg';
import { ReactComponent as ActivityLogsClock } from '../../../../assets/svg/blocker-modals/ActivityLogsClock.svg';
import { ReactComponent as ActivityLogsUser } from '../../../../assets/svg/blocker-modals/ActivityLogsUser.svg';
import { ReactComponent as ENTAnalytics } from '../../../../assets/svg/blocker-modals/ENTAnalytics.svg';

// SLA
import SLAIntegrationImg from '../../../../assets/img/blocker-modals/SLAIntegrate.webp';
import { ReactComponent as Clock } from '../../../../assets/svg/blocker-modals/Clock.svg';
import { ReactComponent as Eye } from '../../../../assets/svg/blocker-modals/Eye.svg';
import { ReactComponent as UserShield } from '../../../../assets/svg/blocker-modals/UserShield.svg';
import { ReactComponent as Robot } from '../../../../assets/svg/blocker-modals/Robot.svg';
import { ReactComponent as SmileyHeart } from '../../../../assets/svg/blocker-modals/SmileyHeart.svg';
import TickTokIcon from '../../../../icons/TickTokIcon';

// SSO
import SSOIntegrationImg from '../../../../assets/img/blocker-modals/SSOIntegration.webp';
import UpArrowIcon from '../../../../icons/UpArrow';

type PurchaseBlockerFeature = {
  id: number;
  title: string;
  description?: string;
  icon: React.ReactElement;
  iconProps?: Record<string, unknown>;
  iconWrapperStyles?: CSSProperties;
  skipIconDefaults?: boolean /** If true the icon is rendered as-is without cloneElement color/size defaults. */;
};

export type PuchaseBlockerConstantsType = {
  blockerImage: string;
  blockerImageHideDT?: string;
  blockerIcon: React.ReactElement;
  blockerIconLabel?: string | ((orgName: string) => string) /** Static label or a factory that receives orgName. */;
  blockerIconWrapperStyles?: CSSProperties;
  blockerIconLabelStyles?: CSSProperties;
  blockerTitle: string;
  blockerTitleSubtext?: string;
  blockerSubtitle?: string;
  features: PurchaseBlockerFeature[];
  purchaseNowText: string;
  addonKey?: PlanAddonTypes;
  enterpriseContactMessage?: string /** If present and addonKey is absent, opens a WhatsApp link to MAIN_IN_SUPPORT_NUMBER when hideDTDetails = false (SSO enterprise flow). */;
  purchaseButtonText?: string /** Overrides the default "Purchase Now" button label. */;
  contactUsButtonText?: string /** Overrides the default "Contact Us" button label. */;
  demoVideoLink?: string /** If present, a Demo button is shown when hideDTDetails = false. */;
};

const PILL_WRAPPER: CSSProperties = {
  border: '1px solid #E0E0E9',
  borderRadius: '50px',
  padding: '4px 8px 4px 4px',
  background: '#fff',
  gap: '5px',
};

const PILL_LABEL: CSSProperties = {
  fontSize: '12px',
  fontWeight: 500,
  color: '#2d2d2d',
};

export const PURCHASE_BLOCKER_MODAL_CONSTANTS: Record<PurchaseBlockerModalType, PuchaseBlockerConstantsType> = {
  [PurchaseBlockerModalType.ZOHO]: {
    blockerImage: ZohoPaywallImage,
    blockerIcon: createElement(ZohoCrmIcon, { style: { width: '63px', height: '20px' } }),
    blockerTitle: 'Setup real time two-way sync between Zoho CRM and DoubleTick',
    features: [
      {
        id: 0,
        title: 'All customers and team data sync',
        description: 'Access complete WhatsApp chat history within Zoho CRM with real time sync',
        icon: createElement(CloudCheckIcon),
      },
      {
        id: 1,
        title: 'Access DoubleTick via iFrame or Widget',
        description: 'Chat with your customers directly from Zoho using DoubleTick’s iFrame or widget',
        icon: createElement(TopSideIcon),
      },
      {
        id: 2,
        title: 'Setup automation on Zoho’s events',
        description: 'Automate communication and workflows using Zoho’s bot components in DoubleTick',
        icon: createElement(FlowChartIcon),
      },
    ],
    purchaseNowText: "I'm interested in buying Zoho CRM Addon",
    addonKey: PlanAddonTypes.ZOHO_CRM,
  },

  [PurchaseBlockerModalType.LEADSQUARED]: {
    blockerImage: LeadSquaredPaywallImage,
    blockerIcon: createElement(LeadSquaredIcon, { style: { width: '16px', height: '16px' } }),
    blockerIconLabel: 'Leadsquared Integration',
    blockerIconWrapperStyles: PILL_WRAPPER,
    blockerIconLabelStyles: PILL_LABEL,
    blockerTitle: 'Manage your WhatsApp communication directly from Leadsquared',
    features: [
      {
        id: 0,
        title: 'Auto-send WhatsApp messsages to new leads',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
      {
        id: 1,
        title: 'Sync lead stage and status with automation triggers',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
      {
        id: 2,
        title: 'View complete chat history inside LeadSquared',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
      {
        id: 3,
        title: 'All leads synced in real time',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
      {
        id: 4,
        title: 'Boost response rates and coversions',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
    ],
    purchaseNowText: "I'm interested in buying LeadSquared Addon",
    addonKey: PlanAddonTypes.LeadSquaredIntegration,
  },

  [PurchaseBlockerModalType.SALESFORCE]: {
    blockerImage: SalesforcePaywallImage,
    blockerIcon: createElement(SalesforceIcon),
    blockerIconLabel: 'Salesforce Integration',
    blockerIconWrapperStyles: PILL_WRAPPER,
    blockerIconLabelStyles: PILL_LABEL,
    blockerTitle: 'Access DoubleTick directly in Salesforce with complete lead and chat details',
    features: [
      {
        id: 0,
        title: 'Instantly send WhatsApp Message to new leads',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
      {
        id: 1,
        title: 'Sync lead status with automation triggers for leads stage change',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
      {
        id: 2,
        title: 'All Leads synced to Salesforce ',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
      {
        id: 3,
        title: 'Complete chat history',
        icon: createElement(SingleTickIcon),
        iconWrapperStyles: {
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '6px',
        },
        iconProps: { size: 20 },
      },
    ],
    purchaseNowText: "I'm interested in buying Salesforce Addon",
    addonKey: PlanAddonTypes.Salesforce,
  },

  [PurchaseBlockerModalType.WOOCOMMERCE]: {
    blockerImage: WoocommerceDoubletickImage,
    blockerImageHideDT: WoocommerceImage,
    blockerIcon: createElement(WooCommerceIcon),
    blockerIconLabel: (orgName: string) => `Integrate ${orgName} with WooCommerce`,
    blockerIconWrapperStyles: { ...PILL_WRAPPER, padding: '4px 8px 4px 4px' },
    blockerTitle: 'Get WooCommerce integration and boost your sales',
    features: [
      {
        id: 0,
        title: 'Automated Cart Recovery',
        description: 'Win back lost sales with automated WhatsApp reminders.',
        icon: createElement(WooIcon1),
        skipIconDefaults: true,
      },
      {
        id: 1,
        title: 'Simplify Store Management',
        description: 'Automate order processing and customer communication.',
        icon: createElement(WooIcon2),
        skipIconDefaults: true,
      },
      {
        id: 2,
        title: 'Increase Customer Engagement',
        description: 'Send event-triggered messages to boost engagement.',
        icon: createElement(WooIcon3),
        skipIconDefaults: true,
      },
      {
        id: 3,
        title: 'Boost Sales with Personalized Messages',
        description: 'Deliver tailored offers directly to your customers.',
        icon: createElement(WooIcon4),
        skipIconDefaults: true,
      },
    ],
    purchaseNowText: "I'm interested in buying WooCommerce Addon",
    addonKey: PlanAddonTypes.WooCommerce,
    contactUsButtonText: 'Get Integration Now!',
    demoVideoLink: 'https://www.youtube.com/watch?v=fIvl64JlxoY',
  },

  [PurchaseBlockerModalType.ACTIVITY_LOGS]: {
    blockerImage: ActivityLogsPNG,
    blockerIcon: createElement(ActivityLogsIcon),
    blockerIconLabel: 'Activity Logs',
    blockerIconWrapperStyles: { ...PILL_WRAPPER, color: '#2d2d2d' },
    blockerTitle: 'Every Action Logged. Always Accessible.',
    blockerSubtitle: 'Stay informed with a clean, chronological log of all key actions performed in your workspace.',
    features: [
      {
        id: 0,
        title: 'Timeline View',
        description: 'All activities shown in a clean, date-wise timeline.',
        icon: createElement(ActivityLogsClock),
        iconWrapperStyles: { width: '32px', height: '32px' },
        skipIconDefaults: true,
      },
      {
        id: 1,
        title: 'Advanced Search & Filters',
        description: 'Search by keyword, user, action, or date.',
        icon: createElement(ENTFilter),
        iconWrapperStyles: { width: '32px', height: '32px' },
        skipIconDefaults: true,
      },
      {
        id: 2,
        title: 'Live Activity Updates',
        description: 'Logs update in real-time, no refresh needed.',
        icon: createElement(ENTAnalytics),
        iconWrapperStyles: { width: '32px', height: '32px' },
        skipIconDefaults: true,
      },
      {
        id: 3,
        title: 'User & Action Details',
        description: 'See who did what, when, and from where.',
        icon: createElement(ActivityLogsUser),
        iconWrapperStyles: { width: '32px', height: '32px' },
        skipIconDefaults: true,
      },
    ],
    purchaseNowText: "I'm interested in buying Activity Logs Addon",
    addonKey: PlanAddonTypes.ActivityLogs,
    purchaseButtonText: 'Unlock Full Access',
  },

  [PurchaseBlockerModalType.SLA]: {
    blockerImage: 'https://catalogue-cdn.quicksell.co/doubletick/sla_file.webp',
    blockerImageHideDT: SLAIntegrationImg,
    blockerIcon: createElement(TickTokIcon),
    blockerIconLabel: 'Service Level Agreement (SLA)',
    blockerIconWrapperStyles: { ...PILL_WRAPPER, marginBottom: '16px', marginTop: '16px', background: 'transparent' },
    blockerTitle: 'Set response timer for all customer chats',
    blockerTitleSubtext: 'with SLA (Service level agreement)',
    features: [
      { id: 0, title: 'Set desired response time for your team', icon: createElement(Clock), skipIconDefaults: true },
      { id: 1, title: 'Monitor breached conversations', icon: createElement(Eye), skipIconDefaults: true },
      { id: 2, title: 'Increase team accountability', icon: createElement(UserShield), skipIconDefaults: true },
      { id: 3, title: 'Trigger a bot if response time has passed', icon: createElement(Robot), skipIconDefaults: true },
      { id: 4, title: 'Enhance customer satisfaction', icon: createElement(SmileyHeart), skipIconDefaults: true },
    ],
    purchaseNowText: "I'm interested in Enterprise Plan",
    addonKey: PlanAddonTypes.SlaAddon,
  },

  [PurchaseBlockerModalType.SSO]: {
    blockerImage: SSOIntegrationImg,
    blockerIcon: createElement(UpArrowIcon),
    blockerIconLabel: (orgName: string) => `Upgrade to ${orgName} Enterprise plan`,
    blockerIconWrapperStyles: { ...PILL_WRAPPER, padding: '4px 8px', gap: '8px' },
    blockerTitle: 'Enable Single Sign on (SSO)',
    blockerTitleSubtext: "for Faster login and centralised management of your team's details",
    features: [
      {
        id: 0,
        title: 'One-click logins save time and eliminate OTP fatigue',
        icon: createElement(SingleTickIcon),
        iconProps: { width: 20, height: 20, color: '#3f906d' },
      },
      {
        id: 1,
        title: 'Easy user provisioning and compliance auditing',
        icon: createElement(SingleTickIcon),
        iconProps: { width: 20, height: 20, color: '#3f906d' },
      },
      {
        id: 2,
        title: 'Reduce risks with centralized authentication',
        icon: createElement(SingleTickIcon),
        iconProps: { width: 20, height: 20, color: '#3f906d' },
      },
      {
        id: 3,
        title: 'Connect using Google Workspace, Microsoft Entra ID or Okta',
        icon: createElement(SingleTickIcon),
        iconProps: { width: 20, height: 20, color: '#3f906d' },
      },
    ],
    purchaseNowText: "I'm interested in Enterprise Plan",
    enterpriseContactMessage: "I'm interested in DoubleTick Enterprise",
    contactUsButtonText: 'Upgrade to Enterprise Plan',
  },
};
